import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { getSignerContractByAddress, Deposit, DepositState, formatAmount, getStateString, formatTimestamp } from '../contracts/contract';
import { NetworkConfig } from '../config/networks';
import { trustlineService } from '../services/trustline';
import { getTokenSymbolDisplay } from '../utils/tokenSymbol';
import './DepositList.css';

interface DepositListProps {
  deposits: Deposit[];
  signer: ethers.Signer;
  network: NetworkConfig;
  userAddress: string;
  provider: ethers.Provider;
  contractAddress: string;
  arbiterAddress: string;
  onAction: (depositId: number, expectedState: number) => void;
  onInitiateTransfer?: () => void;
}

const DepositList: React.FC<DepositListProps> = ({ 
  deposits, 
  signer, 
  network, 
  userAddress, 
  provider, 
  contractAddress,
  arbiterAddress,
  onAction,
  onInitiateTransfer
}) => {
  const [loading, setLoading] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [tokenSymbols, setTokenSymbols] = useState<Map<string, string>>(new Map());
  const [showNotificationModal, setShowNotificationModal] = useState<number | null>(null);
  const [copySuccess, setCopySuccess] = useState<number | null>(null);
  const [showEmailDomainModal, setShowEmailDomainModal] = useState<number | null>(null);
  const [emailsOrDomains, setEmailsOrDomains] = useState('');
  const [configuringPolicy, setConfiguringPolicy] = useState(false);
  const [depositPolicies, setDepositPolicies] = useState<Map<number, any>>(new Map());

  // Fetch token symbols when deposits change
  useEffect(() => {
    const fetchTokenSymbols = async () => {
      if (!provider || deposits.length === 0) return;

      const newSymbols = new Map<string, string>();
      const uniqueTokens = Array.from(new Set(deposits.map(deposit => deposit.token)));
      
      for (const tokenAddress of uniqueTokens) {
        const symbol = await getTokenSymbolDisplay(tokenAddress, provider, network);
        newSymbols.set(tokenAddress, symbol);
      }
      
      setTokenSymbols(newSymbols);
    };

    fetchTokenSymbols();
  }, [provider, deposits, network]);

  // Fetch policies for deposits when they change
  useEffect(() => {
    const fetchPolicies = async () => {
      if (!provider || !network || !contractAddress || deposits.length === 0) return;

      const newPolicies = new Map<number, any>();
      
      // Only fetch policies for deposits in Deposited state
      const activeDeposits = deposits.filter(d => d.state === DepositState.Deposited);
      
      for (const deposit of activeDeposits) {
        try {
          const response = await trustlineService.fetchPolicyForWithdraw(
            network.chainId,
            deposit.beneficiary,
            contractAddress,
            deposit.id
          );
          
          if (response && 'result' in response && response.result.success) {
            newPolicies.set(deposit.id, response.result);
          }
        } catch (error) {
          // Silently fail - policy might not be configured yet
          console.debug(`Failed to fetch policy for deposit ${deposit.id}:`, error);
        }
      }
      
      setDepositPolicies(newPolicies);
    };

    fetchPolicies();
  }, [provider, network, contractAddress, deposits]);

  const handleRelease = async (depositId: number) => {
    setLoading(depositId);
    setError('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.release(depositId);
      await tx.wait();
      onAction(depositId, DepositState.Released);
    } catch (err) {
      setError('Release failed: ' + (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const handleWithdraw = async (depositId: number) => {
    setLoading(depositId);
    setError('');

    try {
      // Trustline validation for withdraw (beneficiary)
      const isApproved = await trustlineService.validateWithdraw(
        network.chainId,
        userAddress,
        contractAddress,
        depositId
      );

      if (!isApproved) {
        throw new Error('Withdraw transaction not approved by Trustline trusted providers');
      }

      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.withdraw(depositId);
      await tx.wait();
      onAction(depositId, DepositState.Released);
    } catch (err) {
      setError('Withdraw failed: ' + (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const handleRefund = async (depositId: number) => {
    setLoading(depositId);
    setError('');

    try {
      // Trustline validation for refund is disabled
      // const isApproved = await trustlineService.validateRefund(
      //   network.chainId,
      //   userAddress,
      //   network.contractAddress,
      //   depositId
      // );

      // if (!isApproved) {
      //   throw new Error('Refund transaction not approved by Trustline trusted providers');
      // }

      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.refund(depositId);
      await tx.wait();
      onAction(depositId, DepositState.Refunded);
    } catch (err) {
      setError('Refund failed: ' + (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const canRelease = (deposit: Deposit): boolean => {
    return deposit.state === DepositState.Deposited && 
           (arbiterAddress.toLowerCase() === userAddress.toLowerCase());
  };

  const canWithdraw = (deposit: Deposit): boolean => {
    return deposit.state === DepositState.Deposited && 
           (deposit.beneficiary.toLowerCase() === userAddress.toLowerCase());
  };

  const canRefund = (deposit: Deposit): boolean => {
    return deposit.state === DepositState.Deposited && 
           ((arbiterAddress.toLowerCase() === userAddress.toLowerCase()) ||
           (deposit.depositor.toLowerCase() === userAddress.toLowerCase()));
  };

  const formatAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if desired
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const getTokenSymbolDisplayLocal = (tokenAddress: string): string => {
    return tokenSymbols.get(tokenAddress) || 'Token';
  };

  const generateNotificationMessage = (deposit: Deposit): string => {
    const amount = formatAmount(deposit.amount);
    const symbol = getTokenSymbolDisplayLocal(deposit.token);
    const url = window.location.href;
    
    return `Dear Beneficiary,\n\nI have sent you ${amount} ${symbol} and you can receive them at:\n${url}`;
  };

  const handleNotifyBeneficiary = (depositId: number) => {
    setShowNotificationModal(depositId);
  };

  const handleCloseModal = useCallback(() => {
    setShowNotificationModal(null);
    setCopySuccess(null);
  }, []);

  const handleModalBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  const handleOpenEmailDomainModal = (depositId: number) => {
    setShowEmailDomainModal(depositId);
    setEmailsOrDomains('');
  };

  const handleCloseEmailDomainModal = () => {
    setShowEmailDomainModal(null);
    setEmailsOrDomains('');
    setError('');
  };

  const handleEmailDomainModalBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !configuringPolicy) {
      handleCloseEmailDomainModal();
    }
  };

  const handleConfigurePolicy = async (deposit: Deposit) => {
    if (!emailsOrDomains.trim()) {
      setError('Please enter an email or domain');
      return;
    }

    // Validate comma-separated list of emails or domains
    const emailOrDomainList = emailsOrDomains.split(',').map(item => item.trim()).filter(item => item.length > 0);
    
    if (emailOrDomainList.length === 0) {
      setError('Please enter at least one email or domain');
      return;
    }

    // Regex for domain: alphanumeric, hyphens, dots (e.g., example.com, sub.example.com)
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    // Regex for full email: local@domain format
    const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    // Validate each item in the list
    for (const item of emailOrDomainList) {
      const isEmail = item.includes('@');
      const isValid = isEmail ? emailRegex.test(item) : domainRegex.test(item);
      
      if (!isValid) {
        setError(`Invalid ${isEmail ? 'email' : 'domain'}: ${item}. Please use format like "user@example.com" or "example.com"`);
        return;
      }
    }

    setConfiguringPolicy(true);
    setError('');

    try {
      // Configure policy for withdraw transaction
      const response = await trustlineService.configurePolicyForWithdraw(
        network.chainId,
        deposit.beneficiary, // The beneficiary who will withdraw
        contractAddress,
        deposit.id,
        emailsOrDomains.split(',').map(domain => domain.trim()), // Allowed email domains
        signer
      );

      if (response && 'result' in response && response.result.success) {
        // Policy configured successfully
        // Fetch the policy to display it and update isCustomized status
        try {
          const policyResponse = await trustlineService.fetchPolicyForWithdraw(
            network.chainId,
            deposit.beneficiary,
            contractAddress,
            deposit.id
          );
          if (policyResponse && 'result' in policyResponse && policyResponse.result.success) {
            setDepositPolicies(prev => {
              const updated = new Map(prev);
              updated.set(deposit.id, policyResponse.result);
              return updated;
            });
          }
        } catch (err) {
          console.debug('Failed to fetch policy after configuration:', err);
        }
        
        handleCloseEmailDomainModal();
      } else {
        // Extract error message from response if available
        const errorMessage = (response && 'result' in response && 'error' in response.result) 
          ? response.result.error 
          : (response && 'error' in response && response.error?.message)
          ? response.error.message
          : 'Policy configuration failed';
        throw new Error(errorMessage);
      }
    } catch (err) {
      setError('Failed to configure policy: ' + (err as Error).message);
    } finally {
      setConfiguringPolicy(false);
    }
  };

  // Handle ESC key to close modals
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showNotificationModal !== null) {
          handleCloseModal();
        } else if (showEmailDomainModal !== null && !configuringPolicy) {
          handleCloseEmailDomainModal();
        }
      }
    };

    if (showNotificationModal !== null || showEmailDomainModal !== null) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showNotificationModal, showEmailDomainModal, configuringPolicy, handleCloseModal]);

  const handleCopyNotification = async (deposit: Deposit) => {
    const message = generateNotificationMessage(deposit);
    await copyToClipboard(message);
    setCopySuccess(deposit.id);
    setTimeout(() => setCopySuccess(null), 2000); // Clear success state after 2 seconds
  };

  const handleCopyUrlOnly = async () => {
    const url = window.location.href;
    await copyToClipboard(url);
    setCopySuccess(-1); // Use -1 to indicate URL copy
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const canSetEmailDomain = (deposit: Deposit): boolean => {
    if (deposit.state !== DepositState.Deposited || 
        deposit.depositor.toLowerCase() !== userAddress.toLowerCase()) {
      return false;
    }

    // Check if policy exists and if it's customized
    const policy = depositPolicies.get(deposit.id);

    if (policy) {
      // Policy result is stored directly, check isCustomized flag
      // Show button if policy is not customized (isCustomized === false)
      // Also check if customization is allowed (policy might not allow customization)
      const policyData = policy.policy;
      if (policyData?.type !== 'UserAuthenticationPolicy' || policyData?.allowsCustomization !== true) {
        return false;
      }

      if (typeof policy.isCustomized === 'boolean') {
        return policy.isCustomized === false;
      }
    }

    // If no policy fetched yet, do not allow setting (will be checked after fetch)
    return false;
  };

  const canNotifyBeneficiary = (deposit: Deposit): boolean => {
    if (deposit.state !== DepositState.Deposited || 
        deposit.beneficiary.toLowerCase() === userAddress.toLowerCase()) {
      return false;
    }

    // Check if policy exists and has allowed emails/domains configured
    const policy = depositPolicies.get(deposit.id);
    if (policy) {
      const policyData = policy.policy;
      // Check if it's a UserAuthenticationPolicy with allowedEmailDomains
      if (policyData?.type === 'UserAuthenticationPolicy') {
        // Return false if allowedEmailDomains is empty or doesn't exist
        if (!policyData.allowedEmailDomains || policyData.allowedEmailDomains.length === 0) {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    }

    // If no policy fetched yet, do not allow setting (will be checked after fetch)
    return false;
  };

  const renderPolicyInfo = (deposit: Deposit): React.ReactNode => {
    if (!depositPolicies.has(deposit.id) || deposit.state !== DepositState.Deposited) {
      return null;
    }

    const policy = depositPolicies.get(deposit.id);
    // Policy result is stored directly, so access policy data directly
    const policyData = policy?.policy;
    
    if (policyData?.type === 'UserAuthenticationPolicy' && policyData?.allowedEmailDomains?.length > 0) {
      // Format email domains: full emails stay as-is, domains get "*@" prefix
      const formattedEmails = policyData.allowedEmailDomains.map((emailOrDomain: string) => {
        // Check if it's a full email (contains @) or just a domain
        if (emailOrDomain.includes('@')) {
          return emailOrDomain; // Full email, return as-is
        } else {
          return `*@${emailOrDomain}`; // Domain, add *@ prefix
        }
      });

      return (
        <div className="detail-row policy-row">
          <span className="label">Allowed Emails:</span>
          <span className="value policy-value">
            {formattedEmails.join(', ')}
          </span>
        </div>
      );
    }
    
    return null;
  };

  if (deposits.length === 0) {
    return (
      <div className="deposit-list">
        <h3>Deposits</h3>
        <p className="no-deposits">No deposits found</p>
      </div>
    );
  }

  // Sort deposits by ID in descending order (highest first)
  const sortedDeposits = [...deposits].sort((a, b) => {
    return b.id - a.id;
  });

  return (
    <div className="deposit-list">
      <div className="deposit-list-header">
        <h3>Safe Transfers [{deposits.length}]</h3>
        {onInitiateTransfer && (
          <button onClick={onInitiateTransfer} className="initiate-transfer-btn">
            Initiate Safe Transfer
          </button>
        )}
      </div>
      
      {error && <div className="error">{error}</div>}
      
      <div className="deposits-grid">
        {sortedDeposits.map((deposit) => {
          return (
          <div key={deposit.id} className="deposit-card">
            <div className="deposit-header">
              <span className="deposit-id">ID: #{deposit.id}</span>
              <span className={`deposit-state state-${deposit.state}`}>
                {getStateString(deposit.state)}
              </span>
            </div>
            
            <div className="deposit-details">
              <div className="detail-row">
                <span className="label">Amount:</span>
                <span className="value">
                  {formatAmount(deposit.amount)} {getTokenSymbolDisplayLocal(deposit.token)}
                </span>
              </div>
              
              <div className="detail-row">
                <span className="label">Sender:</span>
                <span className={`value ${deposit.depositor.toLowerCase() === userAddress.toLowerCase() ? 'highlight-address' : 'regular-address'}`}>
                  {formatAddress(deposit.depositor)}
                  <button 
                    className="copy-btn" 
                    onClick={() => copyToClipboard(deposit.depositor)}
                    title="Copy full address"
                  >
                    📋
                  </button>
                </span>
              </div>
              
              <div className="detail-row">
                <span className="label">Beneficiary:</span>
                <span className={`value ${deposit.beneficiary.toLowerCase() === userAddress.toLowerCase() ? 'highlight-address' : 'regular-address'}`}>
                  {formatAddress(deposit.beneficiary)}
                  <button 
                    className="copy-btn" 
                    onClick={() => copyToClipboard(deposit.beneficiary)}
                    title="Copy full address"
                  >
                    📋
                  </button>
                </span>
              </div>
              
              {deposit.token !== ethers.ZeroAddress && (
                <div className="detail-row">
                  <span className="label">Token:</span>
                  <span className="value">{formatAddress(deposit.token)}</span>
                </div>
              )}
              
              <div className="detail-row">
                <span className="label">Sent at:</span>
                <span className="value">{formatTimestamp(deposit.depositTimestamp)}</span>
              </div>
              
              <div className="detail-row">
                <span className="label">Completed at:</span>
                <span className="value">{formatTimestamp(deposit.completionTimestamp)}</span>
              </div>
              
              {renderPolicyInfo(deposit)}
            </div>
            
            {deposit.state === DepositState.Deposited && (
              <>
                <div className="deposit-actions">
                  {canRelease(deposit) && (
                    <button
                      onClick={() => handleRelease(deposit.id)}
                      disabled={loading === deposit.id}
                      className="action-btn release-btn"
                    >
                      {loading === deposit.id ? 'Processing...' : 'Release'}
                    </button>
                  )}
                  
                  {canWithdraw(deposit) && (
                    <button
                      onClick={() => handleWithdraw(deposit.id)}
                      disabled={loading === deposit.id}
                      className="action-btn withdraw-btn"
                    >
                      {loading === deposit.id ? 'Processing...' : 'Receive'}
                    </button>
                  )}
                  
                  {canRefund(deposit) && (
                    <button
                      onClick={() => handleRefund(deposit.id)}
                      disabled={loading === deposit.id}
                      className="action-btn refund-btn"
                    >
                      {loading === deposit.id ? 'Processing...' : 'Cancel'}
                    </button>
                  )}
                  
                  {canSetEmailDomain(deposit) && (
                    <button
                      onClick={() => handleOpenEmailDomainModal(deposit.id)}
                      className="action-btn set-email-domain-btn"
                    >
                      {loading === deposit.id ? 'Processing...' : 'Set Emails'}
                    </button>
                  )}
                  
                  {canNotifyBeneficiary(deposit) && (
                    <button
                      onClick={() => handleNotifyBeneficiary(deposit.id)}
                      className="action-btn notify-btn"
                    >
                      {loading === deposit.id ? 'Processing...' : 'Notify Beneficiary'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          );
        })}
      </div>

      {/* Notification Modal */}
      {showNotificationModal !== null && (() => {
        const deposit = sortedDeposits.find(d => d.id === showNotificationModal);
        if (!deposit || !canNotifyBeneficiary(deposit)) return null;
        
        return (
          <div 
            className="notification-modal-backdrop" 
            onClick={handleModalBackdropClick}
          >
            <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
              <div className="notification-modal-header">
                <h3>Notify Beneficiary</h3>
              </div>
              <div className="notification-modal-content">
                <div className="notification-header">
                  <span>Copy this message to send to the beneficiary:</span>
                  <button
                    onClick={() => handleCopyNotification(deposit)}
                    className="copy-notification-btn"
                    title="Copy full message"
                  >
                    {copySuccess === deposit.id ? '✓' : '📋'}
                  </button>
                </div>
                <textarea
                  readOnly
                  value={generateNotificationMessage(deposit)}
                  className="notification-text"
                  rows={4}
                />
                <div className="copy-url-row">
                  <span className="copy-url-label">Copy URL only</span>
                  <button
                    onClick={handleCopyUrlOnly}
                    className="copy-url-only-btn"
                    title="Copy URL only"
                  >
                    {copySuccess === -1 ? '✓' : '📋'}
                  </button>
                </div>
              </div>
              <div className="notification-modal-footer">
                <button
                  onClick={handleCloseModal}
                  className="modal-ok-btn"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Email Domain Configuration Modal */}
      {showEmailDomainModal !== null && (() => {
        const deposit = sortedDeposits.find(d => d.id === showEmailDomainModal);
        if (!deposit || !canSetEmailDomain(deposit)) return null;
        
        return (
          <div 
            className="notification-modal-backdrop" 
            onClick={handleEmailDomainModalBackdropClick}
          >
            <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
              <div className="notification-modal-header">
                <h3>Set Emails or Domains</h3>
              </div>
              <div className="notification-modal-content">
                <p className="email-domain-description">
                  Configure the emails or domains required for this transfer. The beneficiary will need to verify their email address from the specified emails or domains to withdraw the funds.
                </p>
                <div className="form-group">
                  <label htmlFor="email-domain">Emails / Domains:</label>
                  <input
                    id="email-domain"
                    type="text"
                    value={emailsOrDomains}
                    onChange={(e) => {
                      setEmailsOrDomains(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="bob@example.com, another.com"
                    disabled={configuringPolicy}
                    className={error ? 'error' : ''}
                    autoFocus
                  />
                  <small className="input-hint">Enter the emails or domains separated by commas.</small>
                </div>
                {error && <div className="error">{error}</div>}
              </div>
              <div className="notification-modal-footer">
                <button
                  onClick={handleCloseEmailDomainModal}
                  className="modal-cancel-btn"
                  disabled={configuringPolicy}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleConfigurePolicy(deposit)}
                  className="modal-ok-btn"
                  disabled={configuringPolicy || !emailsOrDomains.trim()}
                >
                  {configuringPolicy ? 'Configuring...' : 'Configure'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DepositList;
